import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import { ExamBoardStudent } from '../../../../features/curator/examAnalysisSlice'
import { capitalize } from '../../../../utils/functions'
import { tableStyles, tone, ui } from '../../../../utils/uiTokens'

// Міндетті пәндер: Қазақстан тарихы, оқу және математикалық сауаттылық
const REQUIRED = [
    { code: '03', short: 'ҚТ' },
    { code: '30', short: 'МС' },
    { code: '29', short: 'ОС' },
]
const REQUIRED_CODES = REQUIRED.map((r) => r.code)

export const pointOf = (s: ExamBoardStudent, code: string): number | null =>
    s.subjects.find((x) => x.code === code)?.point ?? null

interface Props {
    rows: ExamBoardStudent[]
    subjectCode: string
    subjectName: string
    groupOf: Map<number, string>
}

type SortKey = 'subject' | 'total'

const ExamBoardTable: React.FC<Props> = ({ rows, subjectCode, subjectName, groupOf }) => {
    const { formatMessage, locale } = useIntl()
    const L = capitalize(locale)
    const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'subject', desc: true })

    const second = (s: ExamBoardStudent) =>
        s.subjects.find((x) => x.code !== subjectCode && !REQUIRED_CODES.includes(x.code)) ?? null
    const hasSecond = rows.some((s) => second(s))
    // предмет ментора уже в своей колонке - второй раз не показываем
    const required = REQUIRED.filter((r) => r.code !== subjectCode && rows.some((s) => pointOf(s, r.code) !== null))

    const value = (s: ExamBoardStudent) => (sort.key === 'subject' ? pointOf(s, subjectCode) ?? -1 : s.totalScore)
    const sorted = [...rows].sort((a, b) => {
        if (a.passed !== b.passed) return a.passed ? -1 : 1
        const d = value(a) - value(b)
        return sort.desc ? -d : d
    })

    const toggle = (key: SortKey) => setSort((s) => ({ key, desc: s.key === key ? !s.desc : true }))
    const arrow = (key: SortKey) => (sort.key === key ? (sort.desc ? ' ↓' : ' ↑') : '')
    const cell = { fontSize: '16px' }
    const center = { ...cell, textAlign: 'center' as const }
    const own = { backgroundColor: ui.pinkSoft }

    return (
        <TableContainer component={Paper} sx={tableStyles.container} elevation={0}>
            <Table sx={{ minWidth: 640 }}>
                <TableHead sx={{ ...tableStyles.head, '& .MuiTableCell-root': { ...tableStyles.head['& .MuiTableCell-root'], fontSize: '16px', textTransform: 'none', letterSpacing: 0 } }}>
                    <TableRow>
                        <TableCell sx={cell}>№</TableCell>
                        <TableCell sx={cell}>{formatMessage({ id: 'ebColName' })}</TableCell>
                        <TableCell sx={cell}>{formatMessage({ id: 'ebColGroup' })}</TableCell>
                        <TableCell sx={{ ...center, ...own, cursor: 'pointer', color: `${ui.pink} !important` }} onClick={() => toggle('subject')}>
                            {subjectName}{arrow('subject')}
                        </TableCell>
                        {hasSecond && <TableCell sx={center}>{formatMessage({ id: 'ebColSecond' })}</TableCell>}
                        {required.map((r) => <TableCell key={r.code} sx={center}>{r.short}</TableCell>)}
                        <TableCell sx={{ ...center, cursor: 'pointer' }} onClick={() => toggle('total')}>
                            {formatMessage({ id: 'ebColTotal' })}{arrow('total')}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sorted.map((s, i) => {
                        const p = pointOf(s, subjectCode)
                        const sec = second(s)
                        return (
                            <TableRow key={s.userId} sx={{ ...tableStyles.row, '& .MuiTableCell-root': { ...tableStyles.row['& .MuiTableCell-root'], ...cell, ...(s.passed ? {} : { color: ui.muted }) } }}>
                                <TableCell sx={cell}>{i + 1}</TableCell>
                                <TableCell sx={{ ...cell, fontWeight: 600 }}>
                                    {s.lastName} {s.firstName}
                                    {!s.passed && (
                                        <Box component="span" sx={{ ml: 1, px: 1, py: '2px', borderRadius: '8px', fontSize: '16px', fontWeight: 400,
                                            color: tone.neutral.text, backgroundColor: tone.neutral.bg }}>
                                            {formatMessage({ id: 'ebNotPassed' })}
                                        </Box>
                                    )}
                                </TableCell>
                                <TableCell sx={cell}>{groupOf.get(s.userId) ?? ''}</TableCell>
                                <TableCell sx={{ ...center, ...own, fontWeight: 700 }}>{s.passed ? p ?? '—' : ''}</TableCell>
                                {hasSecond && (
                                    <TableCell sx={center}>
                                        {sec ? `${(sec as any)[`name${L}`] || sec.nameKz || sec.code} ${sec.point}` : ''}
                                    </TableCell>
                                )}
                                {required.map((r) => (
                                    <TableCell key={r.code} sx={center}>{pointOf(s, r.code) ?? ''}</TableCell>
                                ))}
                                <TableCell sx={{ ...center, fontWeight: 700 }}>{s.passed ? s.totalScore : ''}</TableCell>
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default ExamBoardTable
